import React from 'react';
import type { Profile } from '../types/profile';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { SUBSCRIPTION_LIMITS } from '../config/subscriptionTiers';

interface ProfileSelectorProps {
  profiles: Profile[];
  currentProfile: Profile | null;
  setCurrentProfile: (profile: Profile) => void;
  showAddNew?: boolean;
}

export const ProfileSelector: React.FC<ProfileSelectorProps> = ({
  profiles,
  currentProfile,
  setCurrentProfile,
  showAddNew = true
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tier, showUpgradeModal } = useSubscription();
  
  // Check if we're on the new profile page
  const isNewProfilePage = new URLSearchParams(location.search).get('action') === 'new-profile';

  const handleAddNew = () => {
    const { profileLimit } = SUBSCRIPTION_LIMITS[tier];
    if (profiles.length >= profileLimit) {
      const nextTier = tier === 'FREE' ? 'PRO' : 'BUSINESS_PRO';
      showUpgradeModal(`create more than ${profileLimit} profile${profileLimit === 1 ? '' : 's'}`, nextTier);
      return;
    }
    navigate('/settings?action=new-profile');
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'new') {
      handleAddNew();
      return;
    }
    
    const selected = profiles.find(p => p._id === value);
    if (selected) {
      setCurrentProfile(selected);
      // If we're on the settings page, clear the new-profile action
      if (location.pathname === '/settings') {
        navigate('/settings', { replace: true });
      }
    }
  };

  return (
    <div className="flex items-center space-x-4">
      <select
        value={isNewProfilePage ? 'new' : (currentProfile?._id || '')}
        onChange={handleChange}
        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
      >
        <option value="" disabled>Select Profile</option>
        {profiles.map((profile) => (
          <option key={profile._id} value={profile._id || ''}>
            {profile.friendlyName || profile.name || 'Unnamed Profile'}
          </option>
        ))}
        {showAddNew && (
          <option value="new">+ Add New Profile</option>
        )}
      </select>
    </div>
  );
};
